import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import Loader from 'ui/Loader/Loader';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import Tabs from 'ui/Tabs/Tabs';
import accountsStore from 'store/accountsStore';
import MobileSubtitle from 'ui/MobileSubtitle/MobileSubtitle';
import { WarningModal } from 'components/modals';
import { IAccount } from 'store/accounts.model';
import MobileMoneyTransfer from './tabs/MobileMoneyTransfer/MobileMoneyTransfer';
import MMFileUpload from './tabs/MobileMoneyBulk/subTabs/FileUpload/MMFileUpload';
import ManualEntry from './tabs/MobileMoneyBulk/subTabs/ManualEntry/ManualEntry';
import styles from '../Transfers/Transfers.module.css';

const tabsOptions = [{ label: 'Mobile Money Transfer' }, { label: 'Mobile Money Bulk Transfer' }];
const innerTabsOptions = [
  { label: 'File Upload', component: MMFileUpload },
  { label: 'Manual Entry', component: ManualEntry },
];

const MobileMoney = () => {
  const [activeTab, setActiveTab] = useState<number | false>(0);
  const [activeInnerTab, setActiveInnerTab] = useState<number | false>(0);
  const [nextTab, setNextTab] = useState<number | false>(0);
  const [nextInnerTab, setNextInnerTab] = useState<number | false>(false);
  const [loadingData, setLoadingData] = useState(false);
  const [accountsList, setAccountsList] = useState<IAccount[]>([]);
  const [isFormDirty, setFormDirty] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [simpleWarning, setSimpleWarning] = useState<string>();
  const [bulkTransfersState, setBulkTransfersState] = useState<'adding' | 'confirmation' | 'report'>('adding');
  const openAddBulkTransfersForm = useCallback(() => setBulkTransfersState('adding'), []);

  const downMdScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
  const { accounts: storeAccounts } = accountsStore;

  useEffect(() => setAccountsList(storeAccounts.filter(({ currency }) => currency === 'GHS')), [storeAccounts]);
  interface IIocationState {
    tab: number | false;
    innerTab: number | false;
    data: Record<string, string>;
  }

  const location = useLocation<IIocationState>();

  useEffect(() => {
    if (downMdScreen) {
      setActiveTab(false);
      setActiveInnerTab(false);
    } else {
      setActiveTab(nextTab);
      setActiveInnerTab(nextInnerTab);
    }

    if (location.state) {
      setActiveTab(location.state.tab);
      setActiveInnerTab(location.state.innerTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downMdScreen]);

  useEffect(() => {
    const fetchAccounts = () => {
      if (storeAccounts.length) {
        // accounts loaded already
        return Promise.resolve();
      }
      return accountsStore.fetchAccounts();
    };

    setLoadingData(true);

    Promise.all([fetchAccounts()]).finally(() => {
      setLoadingData(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnChangeTab = useCallback(
    (_event: React.ChangeEvent<unknown>, tabIndex: number) => {
      if (tabIndex !== 1 && nextInnerTab !== false) setNextInnerTab(false);
      if (tabIndex === 1 && nextInnerTab === false) setNextInnerTab(0);
      setNextTab(tabIndex);

      if (isFormDirty) {
        setShowWarning(true);
      } else {
        if (tabIndex !== 1 && nextInnerTab !== false) setActiveInnerTab(false);
        if (tabIndex === 1 && nextInnerTab === false) setActiveInnerTab(0);
        setActiveTab(tabIndex);
        setNextInnerTab(tabIndex === 1 ? 0 : false);
      }
    },
    [isFormDirty, nextInnerTab],
  );

  const handleOnChangeInnerTab = (_event: React.ChangeEvent<unknown>, tabIndex: number) => {
    if (nextTab !== 1) setNextTab(1);
    setNextInnerTab(tabIndex);

    if (isFormDirty) {
      setShowWarning(true);
    } else {
      if (activeTab !== 1) setActiveTab(1);
      setActiveInnerTab(tabIndex);
    }
  };

  const handleClickBack = () => {
    if (isFormDirty) {
      setShowWarning(true);
    } else {
      setActiveTab(false);
      setActiveInnerTab(false);
    }
  };

  const onLeavePage = () => {
    setShowWarning(false);
    setFormDirty(false);
    if (downMdScreen) {
      setActiveTab(false);
      setActiveInnerTab(false);
    } else {
      setActiveTab(nextTab);
      setActiveInnerTab(nextInnerTab);
    }
  };

  const closeWarning = () => setShowWarning(false);
  const switchDirtyTrue = () => setFormDirty(true);

  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const getTitle = () => {
    if (downMdScreen) {
      if (activeInnerTab !== false) {
        return `Mobile Money ${innerTabsOptions[activeInnerTab].label.slice(19)}`;
      }
      if (activeTab !== false) {
        return `Mobile Money ${tabsOptions[activeTab].label}`;
      }
    }
    return 'Mobile Money';
  };

  const renderTabs = () => {
    if (downMdScreen && activeTab !== false) {
      return null;
    }

    return (
      <>
        <Tabs value={activeTab} options={tabsOptions} onChange={handleOnChangeTab} disabled={loadingData} />
        <Tabs
          value={activeInnerTab}
          options={innerTabsOptions.map((tab) => ({ label: tab.label }))}
          onChange={handleOnChangeInnerTab}
          theme="secondary"
          disabled={loadingData}
        />
      </>
    );
  };

  const showMainForm = !loadingData && (activeTab !== 1 || bulkTransfersState === 'adding');
  const showTitle = !downMdScreen || bulkTransfersState !== 'confirmation';

  return (
    <>
      <DashboardCard className={styles.dashCard}>
        {showTitle && (
          <NavigationTitle
            to="#/"
            layoutType={LayoutType.dashboard}
            onClick={handleClickBack}
            noArrow={activeTab === false}
          >
            {getTitle()}
          </NavigationTitle>
        )}
        {downMdScreen && activeTab === false && <MobileSubtitle>Select Transfer Category</MobileSubtitle>}
        {renderTabs()}
        {loadingData && <Loader />}

        {!loadingData && activeTab === 0 && <MobileMoneyTransfer />}
      </DashboardCard>

      {!loadingData && activeInnerTab === 0 && (
        <MMFileUpload
          accounts={accountsList}
          bulkTransfersState={bulkTransfersState}
          openAddBulkTransfersForm={openAddBulkTransfersForm}
          showMainForm={showMainForm}
          handleChangeForm={switchDirtyTrue}
        />
      )}
      {!loadingData && activeInnerTab === 1 && (
        <ManualEntry accounts={accountsList} showMainForm={showMainForm} handleChangeForm={switchDirtyTrue} />
      )}
      <WarningModal open={showWarning} submitName="Leave Page" onSubmit={onLeavePage} onClose={closeWarning}>
        Entered data will be lost. Are you sure you want to leave page?
      </WarningModal>
      <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
        {simpleWarning}
      </WarningModal>
    </>
  );
};

export default observer(MobileMoney);
